// src/services/pdfTemplates.js
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Безпечна ініціалізація шрифтів
if (pdfMake && pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
  
  // Реєстрація шрифтів
  pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf'
    }
  };
} else {
  console.warn('PDFMake fonts not available, using fallback');
  // Створюємо пустий vfs як fallback
  pdfMake.vfs = {};
  pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf'
    }
  };
}

// Стилі для всіх PDF документів
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    alignment: 'center',
    margin: [0, 0, 0, 10],
    color: '#2c3e50'
  },
  subheader: {
    fontSize: 14,
    bold: true,
    margin: [0, 10, 0, 5],
    color: '#34495e'
  },
  tableHeader: {
    bold: true,
    fontSize: 10,
    color: '#2c3e50',
    fillColor: '#ecf0f1'
  },
  tableCell: {
    fontSize: 9,
    color: '#2c3e50'
  },
  footer: {
    fontSize: 8,
    color: '#7f8c8d',
    alignment: 'center',
    margin: [0, 10, 0, 0]
  },
  highlight: {
    bold: true,
    color: '#e74c3c'
  },
  success: {
    color: '#27ae60'
  }
};

// Загальні функції для всіх шаблонів
const commonUtils = {
  // Форматування дати
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  // Форматування часу
  formatTime: (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  // Заголовок документа
  createHeader: (title, subtitle = null) => {
    const header = [
      {
        text: 'LAINSA',
        style: 'header',
        fontSize: 24,
        color: '#2980b9'
      },
      {
        text: title,
        style: 'header'
      }
    ];

    if (subtitle) {
      header.push({
        text: subtitle,
        style: 'subheader',
        alignment: 'center',
        fontSize: 12
      });
    }

    header.push({
      text: `Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`,
      style: 'footer',
      margin: [0, 5, 0, 15]
    });

    return header;
  },

  // Футер документа
  createFooter: (currentPage, pageCount) => {
    return {
      text: `Página ${currentPage} de ${pageCount} • LAINSA Production Manager • ${new Date().getFullYear()}`,
      style: 'footer',
      margin: [0, 20, 0, 0]
    };
  }
};

// Допоміжні функції для таблиць
function createProductionTable(data, type) {
  if (!data || Object.keys(data).length === 0) {
    return [{
      text: `No hay datos de ${type.toLowerCase()}`,
      italics: true,
      margin: [0, 0, 0, 10]
    }];
  }

  const tableBody = [
    [
      { text: 'DESCRIPCIÓN', style: 'tableHeader' },
      { text: 'PESO (kg)', style: 'tableHeader', alignment: 'right' }
    ]
  ];

  // Словник назв програм
  const PROGRAM_NAMES = {
    pro1: 'Pro.1 - Sábanas',
    pr01: 'Pro.1 - Sábanas',
    pro2: 'Pro.2 - Sábana nueva',
    pr02: 'Pro.2 - Sábana nueva', 
    pro3: 'Pro.3 - Toalla nueva',
    pr03: 'Pro.3 - Toalla nueva',
    pro4: 'Pro.4 - Mantelería',
    pr04: 'Pro.4 - Mantelería',
    pro6: 'Pro.6 - Toallas espigas/rayas',
    pr06: 'Pro.6 - Toallas espigas/rayas',
    pro8: 'Pro.8 - Toallas color',
    pr08: 'Pro.8 - Toallas color',
    pro9: 'Pro.9 - Nórdicos',
    pr09: 'Pro.9 - Nórdicos',
    pro10: 'Pro.10 - Premium',
    pr010: 'Pro.10 - Premium',
    pro14: 'Pro.14 - Albornoces',
    pr014: 'Pro.14 - Albornoces',
    total: 'TOTAL'
  };

  let total = 0;

  // Додаємо рядки для кожної програми
  Object.entries(data)
    .sort(([keyA], [keyB]) => {
      if (keyA === 'total') return 1;
      if (keyB === 'total') return -1;
      return keyA.localeCompare(keyB);
    })
    .forEach(([key, value]) => {
      if (value && value > 0) {
        const programName = PROGRAM_NAMES[key] || key;
        const isTotal = key === 'total';
        
        tableBody.push([
          { text: programName, style: 'tableCell' },
          { 
            text: value.toString(), 
            style: isTotal ? 'highlight' : 'tableCell',
            alignment: 'right'
          }
        ]);

        if (!isTotal) total += Number(value);
      }
    });

  // Якщо немає даних, показуємо повідомлення
  if (tableBody.length === 1) {
    return [{
      text: `No hay datos de ${type.toLowerCase()}`,
      italics: true,
      margin: [0, 0, 0, 10]
    }];
  }

  return [{
    style: 'table',
    table: {
      headerRows: 1,
      widths: ['80%', '20%'],
      body: tableBody
    },
    layout: {
      fillColor: function (rowIndex, node, columnIndex) {
        return (rowIndex === 0) ? '#ecf0f1' : null;
      },
      hLineWidth: function (i, node) {
        return 0.5;
      },
      vLineWidth: function (i, node) {
        return 0.5;
      },
      hLineColor: function (i, node) {
        return '#bdc3c7';
      },
      vLineColor: function (i, node) {
        return '#bdc3c7';
      }
    }
  }];
}

function createMetersTable(contadores) {
  if (!contadores || Object.keys(contadores).length === 0) {
    return [{
      text: 'No hay lecturas de contadores',
      italics: true,
      margin: [0, 0, 0, 10]
    }];
  }

  const tableBody = [
    [
      { text: 'CONTADOR', style: 'tableHeader' },
      { text: 'VALOR', style: 'tableHeader', alignment: 'right' }
    ]
  ];

  // Використовуємо оригінальні назви лічильників як вони є в даних
  Object.entries(contadores).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      tableBody.push([
        { text: key, style: 'tableCell' },
        { text: value.toString(), style: 'tableCell', alignment: 'right' }
      ]);
    }
  });

  return [{
    style: 'table',
    table: {
      headerRows: 1,
      widths: ['70%', '30%'],
      body: tableBody
    },
    layout: {
      fillColor: function (rowIndex, node, columnIndex) {
        return (rowIndex === 0) ? '#ecf0f1' : null;
      }
    }
  }];
}

function createWasherCyclesTable(lav_ciclos) {
  if (!lav_ciclos || Object.keys(lav_ciclos).length === 0) {
    return [{
      text: 'No hay ciclos de lavadoras registrados',
      italics: true,
      margin: [0, 0, 0, 10]
    }];
  }

  const tableBody = [
    [
      { text: 'LAVADORA', style: 'tableHeader' },
      { text: 'CICLOS', style: 'tableHeader', alignment: 'right' }
    ]
  ];

  // Обробляємо цикли пральних машин
  Object.entries(lav_ciclos).forEach(([washer, cycles]) => {
    if (cycles && cycles > 0) {
      tableBody.push([
        { text: washer, style: 'tableCell' },
        { text: cycles.toString(), style: 'tableCell', alignment: 'right' }
      ]);
    }
  });

  // Якщо дані є у форматі "||||" (символи труб)
  const hasPipeData = Object.values(lav_ciclos).some(cycles => 
    typeof cycles === 'string' && cycles.includes('|')
  );

  if (hasPipeData) {
    // Додаємо примітку про формат даних
    tableBody.push([
      { 
        text: 'Nota: Los ciclos se registran con el símbolo "|" (cada "|" representa 1 ciclo)', 
        style: 'tableCell',
        colSpan: 2,
        italics: true,
        color: '#7f8c8d'
      },
      { text: '' }
    ]);
  }

  return [{
    style: 'table',
    table: {
      headerRows: 1,
      widths: ['50%', '50%'],
      body: tableBody
    },
    layout: {
      fillColor: function (rowIndex, node, columnIndex) {
        return (rowIndex === 0) ? '#ecf0f1' : null;
      },
      hLineWidth: function (i, node) {
        return 0.5;
      },
      vLineWidth: function (i, node) {
        return 0.5;
      },
      hLineColor: function (i, node) {
        return '#bdc3c7';
      },
      vLineColor: function (i, node) {
        return '#bdc3c7';
      }
    }
  }];
}

function createAdditionalInfo(report) {
  const additionalContent = [];

  // Цикли пральних машин
  if (report.lav_ciclos && Object.keys(report.lav_ciclos).length > 0) {
    additionalContent.push(
      {
        text: 'CICLOS DE LAVADORAS',
        style: 'subheader',
        margin: [0, 15, 0, 10]
      },
      ...createWasherCyclesTable(report.lav_ciclos)
    );
  }

  // Вага по клієнтах
  if (report.pesoCliente && report.pesoCliente.length > 0) {
    additionalContent.push(
      {
        text: 'PESO POR CLIENTE',
        style: 'subheader',
        margin: [0, 15, 0, 10]
      }
    );

    const clientsTable = {
      style: 'table',
      table: {
        widths: ['40%', '40%', '20%'],
        body: [
          [
            { text: 'CLIENTE', style: 'tableHeader' },
            { text: 'PROGRAMA', style: 'tableHeader' },
            { text: 'PESO (kg)', style: 'tableHeader', alignment: 'right' }
          ]
        ]
      }
    };

    report.pesoCliente.forEach(item => {
      if (item.client && item.weight) {
        clientsTable.table.body.push([
          { text: item.client, style: 'tableCell' },
          { text: item.program || 'N/A', style: 'tableCell' },
          { text: item.weight.toString(), style: 'tableCell', alignment: 'right' }
        ]);
      }
    });

    additionalContent.push(clientsTable);
  }

  // Спостереження
  if (report.observaciones) {
    additionalContent.push(
      {
        text: 'OBSERVACIONES',
        style: 'subheader',
        margin: [0, 15, 0, 10]
      },
      {
        text: report.observaciones,
        style: 'tableCell',
        margin: [0, 0, 0, 10],
        background: '#f8f9fa',
        border: [1, 1, 1, 1],
        borderColor: '#dee2e6',
        fillColor: '#f8f9fa',
        padding: [10, 10, 10, 10]
      }
    );
  }

  return additionalContent;
}

function createFiltersInfo(filters) {
  if (!filters || Object.keys(filters).length === 0) {
    return [];
  }

  const filterText = [];
  
  if (filters.dateFrom) {
    filterText.push(`Desde: ${commonUtils.formatDate(filters.dateFrom)}`);
  }
  
  if (filters.dateTo) {
    filterText.push(`Hasta: ${commonUtils.formatDate(filters.dateTo)}`);
  }
  
  if (filters.shift && filters.shift !== 'all') {
    filterText.push(`Turno: ${filters.shift}`);
  }

  if (filterText.length === 0) return [];

  return [{
    text: `Filtros aplicados: ${filterText.join(' • ')}`,
    style: 'footer',
    margin: [0, 0, 0, 15]
  }];
}

function calculateEfficiency(report) {
  const production = report.tunel?.total || 0;
  const rejection = report.rech_tunel?.total || 0;
  
  if (production === 0) return 'N/A';
  
  const efficiency = ((production - rejection) / production * 100).toFixed(1);
  return `${efficiency}%`;
}

// Базова конфігурація документу
const getBaseDocDefinition = () => ({
  pageSize: 'A4',
  pageMargins: [40, 60, 40, 60],
  defaultStyle: {
    font: 'Roboto'
  },
  styles: styles
});

// Шаблон для звітів операторів
export const operatorReportPDF = (report) => {
  const docDefinition = {
    ...getBaseDocDefinition(),
    header: function(currentPage, pageCount) {
      if (currentPage === 1) return null;
      return {
        text: `Reporte de Turno - Página ${currentPage}`,
        style: 'footer',
        margin: [0, 20, 0, 0]
      };
    },
    footer: commonUtils.createFooter,
    content: [
      // Заголовок
      ...commonUtils.createHeader(
        'REPORTE DE TURNO - PRODUCCIÓN',
        `Fecha: ${commonUtils.formatDate(report.fecha)} • Turno: ${report.turno || report.shift}`
      ),

      // Інформація про операторів
      {
        style: 'table',
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `OPERADORES: ${report.operators?.join(', ') || 'No especificado'}`,
                style: 'subheader',
                border: [false, false, false, true],
                borderColor: '#bdc3c7'
              }
            ]
          ]
        },
        layout: {
          hLineWidth: function(i, node) {
            return 1;
          },
          vLineWidth: function(i, node) {
            return 0;
          }
        }
      },

      // Виробництво тунелю
      {
        text: 'PRODUCCIÓN TÚNEL',
        style: 'subheader',
        margin: [0, 20, 0, 10]
      },
      ...createProductionTable(report.tunel, 'Producción'),

      // Відходи тунелю
      {
        text: 'RECHAZOS TÚNEL',
        style: 'subheader',
        margin: [0, 15, 0, 10]
      },
      ...createProductionTable(report.rech_tunel, 'Rechazos'),

      // Лічильники
      {
        text: 'LECTURAS DE CONTADORES',
        style: 'subheader',
        margin: [0, 15, 0, 10]
      },
      ...createMetersTable(report.cont),

      // Додаткова інформація
      ...createAdditionalInfo(report)
    ]
  };

  return docDefinition;
};

// Експорт для списку звітів
export const operatorReportsPDF = (reports, filters = {}) => {
  const tableBody = [
    [
      { text: 'FECHA', style: 'tableHeader' },
      { text: 'TURNO', style: 'tableHeader' },
      { text: 'OPERADORES', style: 'tableHeader' },
      { text: 'PRODUCCIÓN (kg)', style: 'tableHeader', alignment: 'right' },
      { text: 'RECHAZOS (kg)', style: 'tableHeader', alignment: 'right' },
      { text: 'EFICIENCIA', style: 'tableHeader', alignment: 'right' }
    ]
  ];

  // Додаємо дані звітів
  reports.forEach(report => {
    tableBody.push([
      commonUtils.formatDate(report.fecha),
      report.turno || report.shift || 'N/A',
      report.operators?.join(', ').substring(0, 30) + (report.operators?.join(', ').length > 30 ? '...' : '') || 'N/A',
      { text: (report.tunel?.total || 0).toString(), alignment: 'right' },
      { text: (report.rech_tunel?.total || 0).toString(), alignment: 'right' },
      { 
        text: calculateEfficiency(report), 
        alignment: 'right',
        ...(calculateEfficiency(report) === 'N/A' ? {} : { color: '#27ae60' })
      }
    ]);
  });

  const docDefinition = {
    ...getBaseDocDefinition(),
    footer: commonUtils.createFooter,
    content: [
      ...commonUtils.createHeader(
        'INFORME DE REPORTES DE OPERADOR',
        `Total de reportes: ${reports.length}`
      ),

      // Інформація про фільтри
      ...createFiltersInfo(filters),

      // Таблиця звітів
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: ['15%', '15%', '25%', '15%', '15%', '15%'],
          body: tableBody
        },
        layout: {
          fillColor: function (rowIndex, node, columnIndex) {
            return (rowIndex === 0) ? '#ecf0f1' : null;
          },
          hLineWidth: function (i, node) {
            return 0.5;
          },
          vLineWidth: function (i, node) {
            return 0.5;
          },
          hLineColor: function (i, node) {
            return '#bdc3c7';
          },
          vLineColor: function (i, node) {
            return '#bdc3c7';
          }
        }
      }
    ]
  };

  return docDefinition;
};

export default {
  operatorReportPDF,
  operatorReportsPDF
};


